import { useEffect, useMemo, useState } from 'react';
import { Plus, Save, Trash2, Upload, X, Database, FileText } from 'lucide-react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { PromptKnowledgeCategory, PromptKnowledgeItem } from '../types';

const GLOBAL_KNOWLEDGE_COLLECTION = 'globalKnowledge';

const isDev = Boolean((import.meta as any).env?.DEV);
const logDebug = (...args: any[]) => {
  if (isDev) console.log(...args);
};

interface GlobalKnowledgeItem extends PromptKnowledgeItem {
  updatedAt?: string;
}

const createEmptyForm = (category: PromptKnowledgeCategory = 'documento') => ({
  title: '',
  category,
  tags: '',
  content: '',
  sourceType: 'manual' as 'manual' | 'file',
  sourceName: '',
});

const parseTags = (raw: string): string[] =>
  raw
    .split(',')
    .map((tag) => tag.trim().toLowerCase())
    .filter(Boolean);

const chunkText = (text: string, maxLen = 1300): string[] => {
  const cleaned = text.replace(/\r/g, '').trim();
  if (!cleaned) return [];

  const paragraphs = cleaned
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (paragraphs.length === 0) return [];

  const chunks: string[] = [];
  let current = '';

  for (const paragraph of paragraphs) {
    if (!current) {
      current = paragraph;
      continue;
    }

    if (current.length + paragraph.length + 2 <= maxLen) {
      current += `\n\n${paragraph}`;
    } else {
      chunks.push(current);
      current = paragraph;
    }
  }

  if (current) chunks.push(current);

  if (chunks.length === 1 && chunks[0].length > maxLen) {
    const fallback: string[] = [];
    for (let i = 0; i < chunks[0].length; i += maxLen) {
      fallback.push(chunks[0].slice(i, i + maxLen));
    }
    return fallback;
  }

  return chunks;
};

const toDateString = (value: any): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (value?.toDate) {
    try {
      return value.toDate().toISOString();
    } catch {
      return undefined;
    }
  }
  return undefined;
};

const readFileContent = async (file: File): Promise<string> => {
  const extension = file.name.split('.').pop()?.toLowerCase();

  if (extension === 'docx') {
    const mammoth = await import('mammoth/mammoth.browser');
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return (result.value || '').trim();
  }

  return (await file.text()).trim();
};

interface GlobalRagKnowledgeProps {
  embedded?: boolean;
}

export default function GlobalRagKnowledge({ embedded = false }: GlobalRagKnowledgeProps) {
  const [items, setItems] = useState<Record<string, GlobalKnowledgeItem>>({});
  const [selectedItem, setSelectedItem] = useState<GlobalKnowledgeItem | null>(null);
  const [formData, setFormData] = useState(createEmptyForm());
  const [isLoading, setIsLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);

  const orderedItems = useMemo(() => {
    return Object.values(items).sort((a, b) => {
      const aDate = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const bDate = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return bDate - aDate;
    });
  }, [items]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      logDebug('🔄 [RAG GLOBALE] Caricamento contenuti...');
      const colRef = collection(db, GLOBAL_KNOWLEDGE_COLLECTION);
      const snapshot = await getDocs(colRef);
      const mapped: Record<string, GlobalKnowledgeItem> = {};

      snapshot.forEach((snap) => {
        const data = snap.data() as any;
        mapped[snap.id] = {
          id: snap.id,
          title: String(data.title || 'Contenuto'),
          category: (data.category || 'documento') as PromptKnowledgeCategory,
          content: String(data.content || ''),
          tags: Array.isArray(data.tags) ? data.tags.map((tag: any) => String(tag)) : [],
          sourceType: data.sourceType === 'file' ? 'file' : 'manual',
          sourceName: data.sourceName ? String(data.sourceName) : undefined,
          createdAt: toDateString(data.createdAt) || data.createdAt,
          updatedAt: toDateString(data.updatedAt) || data.updatedAt,
        };
      });

      setItems(mapped);
      logDebug(`✅ [RAG GLOBALE] Contenuti caricati: ${Object.keys(mapped).length}`);
    } catch (error) {
      console.error('❌ [RAG GLOBALE] Errore caricamento:', error);
      alert('Errore durante il caricamento della Biblioteca Condivisa.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleNew = () => {
    setSelectedItem(null);
    setFormData(createEmptyForm());
  };

  const handleSelect = (item: GlobalKnowledgeItem) => {
    setSelectedItem(item);
    setFormData({
      title: item.title,
      category: item.category,
      tags: (item.tags || []).join(', '),
      content: item.content,
      sourceType: item.sourceType || 'manual',
      sourceName: item.sourceName || '',
    });
  };

  const handleCancel = () => {
    if (!selectedItem) {
      setFormData(createEmptyForm(formData.category));
      return;
    }

    handleSelect(selectedItem);
  };

  const buildPayload = () => {
    return {
      title: formData.title.trim(),
      category: formData.category,
      content: formData.content.trim(),
      tags: parseTags(formData.tags),
      sourceType: formData.sourceType,
      sourceName: formData.sourceName.trim() || undefined,
      updatedAt: new Date().toISOString(),
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();

    if (!payload.title || !payload.content) {
      alert('Inserisci almeno titolo e contenuto.');
      return;
    }

    try {
      if (selectedItem) {
        await updateDoc(doc(db, GLOBAL_KNOWLEDGE_COLLECTION, selectedItem.id), payload);
      } else {
        await addDoc(collection(db, GLOBAL_KNOWLEDGE_COLLECTION), {
          ...payload,
          createdAt: new Date().toISOString(),
        });
      }

      await loadData();
      setSelectedItem(null);
      setFormData(createEmptyForm(formData.category));
      alert('Contenuto della Biblioteca Condivisa salvato.');
    } catch (error) {
      console.error('❌ [RAG GLOBALE] Errore salvataggio:', error);
      alert('Errore durante il salvataggio del contenuto.');
    }
  };

  const handleDelete = async (itemId: string) => {
    if (!window.confirm('Confermi l\'eliminazione di questo contenuto globale?')) return;

    try {
      await deleteDoc(doc(db, GLOBAL_KNOWLEDGE_COLLECTION, itemId));
      if (selectedItem?.id === itemId) {
        setSelectedItem(null);
        setFormData(createEmptyForm(formData.category));
      }
      await loadData();
    } catch (error) {
      console.error('❌ [RAG GLOBALE] Errore eliminazione:', error);
      alert('Errore durante l\'eliminazione del contenuto.');
    }
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsImporting(true);
      const content = await readFileContent(file);

      if (!content) {
        alert('Il documento importato è vuoto.');
        return;
      }

      const chunks = chunkText(content);
      if (chunks.length === 0) {
        alert('Nessun contenuto utile trovato nel documento.');
        return;
      }

      const now = new Date().toISOString();
      const sourceName = file.name;
      const titleBase = file.name.replace(/\.[^/.]+$/, '').trim() || 'Documento';
      const tags = parseTags(formData.tags);

      const colRef = collection(db, GLOBAL_KNOWLEDGE_COLLECTION);
      let batch = writeBatch(db);
      let opCount = 0;

      for (let index = 0; index < chunks.length; index += 1) {
        const chunk = chunks[index];
        const itemDoc = doc(colRef);
        const title = chunks.length > 1 ? `${titleBase} (${index + 1}/${chunks.length})` : titleBase;

        batch.set(itemDoc, {
          title,
          category: formData.category,
          content: chunk,
          tags,
          sourceType: 'file',
          sourceName,
          createdAt: now,
          updatedAt: now,
        });

        opCount += 1;
        if (opCount === 450) {
          await batch.commit();
          batch = writeBatch(db);
          opCount = 0;
        }
      }

      if (opCount > 0) {
        await batch.commit();
      }

      await loadData();
      alert(`Import completato: ${chunks.length} contenuti aggiunti alla Biblioteca Condivisa.`);
    } catch (error) {
      console.error('❌ [RAG GLOBALE] Errore import:', error);
      alert('Errore durante l\'import del documento. Supportati: .docx, .txt, .md, .csv, .json, .html');
    } finally {
      setIsImporting(false);
      event.target.value = '';
    }
  };

  if (isLoading) {
    if (embedded) {
      return (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="text-primary text-lg">Caricamento Biblioteca Condivisa...</div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-primary text-xl">Caricamento Biblioteca Condivisa...</div>
      </div>
    );
  }

  const bodyContent = (
    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 ${embedded ? '' : 'container mx-auto max-w-7xl px-4 py-8'}`}>
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Contenuti ({orderedItems.length})</h2>
              <button
                onClick={handleNew}
                className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <Plus size={16} />
                <span>Nuovo</span>
              </button>
            </div>

            <div className="space-y-3 max-h-[34rem] overflow-y-auto pr-1">
              {orderedItems.length === 0 && (
                <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-4">
                  Nessun contenuto condiviso presente. Importa il maxi documento per iniziare.
                </div>
              )}

              {orderedItems.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  className={`p-4 rounded-lg border cursor-pointer transition-colors duration-200 ${
                    selectedItem?.id === item.id
                      ? 'bg-primary/10 border-primary'
                      : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-800 truncate">{item.title}</p>
                      <p className="text-xs text-gray-600 mt-1">
                        {item.category} • {item.sourceType === 'file' ? `file: ${item.sourceName || '-'}` : 'manuale'}
                      </p>
                      {item.tags?.length > 0 && (
                        <p className="text-xs text-gray-600 mt-1 truncate">tag: {item.tags.join(', ')}</p>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.id);
                      }}
                      className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-600 rounded-lg transition-colors duration-200"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold text-gray-800">
              {selectedItem ? 'Modifica Contenuto Condiviso' : 'Nuovo Contenuto Condiviso'}
            </h2>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Titolo</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Titolo contenuto"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Categoria</label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      category: e.target.value as PromptKnowledgeCategory,
                    }))
                  }
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="storia">Storia</option>
                  <option value="curiosita">Curiosita</option>
                  <option value="geografia">Geografia</option>
                  <option value="aneddoto">Aneddoto</option>
                  <option value="frase">Frase</option>
                  <option value="documento">Documento</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Tag</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData((prev) => ({ ...prev, tags: e.target.value }))}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="es. storia, via giordano"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">Contenuto</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))}
                rows={10}
                className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Testo del contenuto globale..."
              />
            </div>

            {selectedItem && (
              <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1">
                <p>Fonte: {selectedItem.sourceType === 'file' ? `file ${selectedItem.sourceName || '-'}` : 'inserimento manuale'}</p>
                {selectedItem.createdAt && <p>Creato: {new Date(selectedItem.createdAt).toLocaleString()}</p>}
                {selectedItem.updatedAt && <p>Aggiornato: {new Date(selectedItem.updatedAt).toLocaleString()}</p>}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <Save size={16} />
                <span>Salva</span>
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <X size={16} />
                <span>Annulla</span>
              </button>

              <label className={`px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2 ${
                isImporting
                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
              }`}>
                <Upload size={16} />
                <span>{isImporting ? 'Import in corso...' : 'Importa Documento'}</span>
                <input
                  type="file"
                  accept=".docx,.txt,.md,.csv,.json,.html"
                  onChange={handleImportFile}
                  className="hidden"
                  disabled={isImporting}
                />
              </label>
            </div>

            <div className="text-xs text-gray-600 bg-blue-50 border border-blue-100 rounded-lg p-3">
              <p className="font-medium text-blue-900 flex items-center gap-1">
                <FileText size={14} /> Import maxi documento
              </p>
              <p className="mt-1">
                Carica il file allegato (anche .docx): viene spezzato in blocchi editabili e salvato nella Biblioteca Condivisa usata da tutti i Genius Loci.
              </p>
            </div>
          </div>
        </div>
  );

  if (embedded) {
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <h3 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
            <Database size={18} />
            Biblioteca Condivisa
          </h3>
          <p className="text-sm text-blue-800 mt-1">
            Archivio comune a tutti gli agenti. Qui carichi contenuti generali (storia, curiosita, geografia, aneddoti, frasi e documenti) da cui tutti possono attingere.
          </p>
        </div>
        {bodyContent}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto max-w-7xl px-4 py-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Database size={24} />
            Biblioteca Condivisa
          </h1>
          <p className="text-gray-600 mt-1">
            Contenuti condivisi da tutti i Genius Loci: storia, curiosita, geografia, aneddoti, frasi e documenti.
          </p>
        </div>
      </div>

      {bodyContent}
    </div>
  );
}
